import React, { useEffect, useMemo, useState, useCallback, Fragment } from 'react';
import {
  Box, Typography, Paper, TextField, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TableSortLabel, IconButton, CircularProgress, Alert,
  ToggleButtonGroup, ToggleButton, Chip, Tooltip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { getManufacturerPurchases, getManufacturerPurchasesAccess } from '../services/api';

// ─── Helpers ────────────────────────────────────────────────────────────────
function fmtMoney(n) {
  if (n == null || n === 0) return '$0';
  const v = parseFloat(n);
  if (Math.abs(v) >= 1e6) return '$' + (v / 1e6).toFixed(2) + 'M';
  if (Math.abs(v) >= 1e3) return '$' + (v / 1e3).toFixed(0) + 'K';
  return '$' + v.toLocaleString('en-US', { maximumFractionDigits: 0 });
}
function fmtMoneyExact(n) {
  if (n == null) return '—';
  return '$' + parseFloat(n).toLocaleString('en-US', { maximumFractionDigits: 0 });
}

const SORT_KEYS = ['canonical', 'received_total', 'ordered_total', 'wip_forecast'];

// ─── Quarterly Sub-row ──────────────────────────────────────────────────────
function QuarterDetail({ row, fiscal_years, metric }) {
  const byQ = metric === 'received' ? row.received_by_q : row.ordered_by_q;
  return (
    <Box sx={{ pl: 6, py: 1, bgcolor: '#f9fafb' }}>
      <Table size="small" sx={{ maxWidth: 900 }}>
        <TableHead>
          <TableRow sx={{ '& th': { fontWeight: 700, fontSize: '0.72rem', color: 'text.secondary', borderBottom: '1px solid #e0e0e0' } }}>
            <TableCell sx={{ width: 80 }}>FY</TableCell>
            <TableCell align="right">Q1<br/><Typography variant="caption" color="text.disabled">Oct–Dec</Typography></TableCell>
            <TableCell align="right">Q2<br/><Typography variant="caption" color="text.disabled">Jan–Mar</Typography></TableCell>
            <TableCell align="right">Q3<br/><Typography variant="caption" color="text.disabled">Apr–Jun</Typography></TableCell>
            <TableCell align="right">Q4<br/><Typography variant="caption" color="text.disabled">Jul–Sep</Typography></TableCell>
            <TableCell align="right">FY Total</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {fiscal_years.map((fy) => {
            const qs = [1, 2, 3, 4].map((q) => byQ[`${fy}_Q${q}`] || 0);
            const total = qs.reduce((a, b) => a + b, 0);
            return (
              <TableRow key={fy}>
                <TableCell sx={{ fontWeight: 600 }}>{fy}</TableCell>
                {qs.map((v, i) => (
                  <TableCell key={i} align="right" sx={{ fontFamily: 'monospace', color: v === 0 ? 'text.disabled' : 'text.primary' }}>
                    <Tooltip title={fmtMoneyExact(v)}>
                      <span>{fmtMoney(v)}</span>
                    </Tooltip>
                  </TableCell>
                ))}
                <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
                  {fmtMoney(total)}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {row.variants_count > 1 && (
        <Box sx={{ mt: 1, pl: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Merged variants: {row.variants.join(', ')}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

// ─── Main page ──────────────────────────────────────────────────────────────
export default function ManufacturerPurchases() {
  const [allowed, setAllowed] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState('');
  const [metric, setMetric] = useState('received');
  const [sortBy, setSortBy] = useState('received_total');
  const [sortDir, setSortDir] = useState('desc');
  const [expanded, setExpanded] = useState(new Set());

  // Access check first
  useEffect(() => {
    let cancelled = false;
    getManufacturerPurchasesAccess()
      .then((r) => { if (!cancelled) setAllowed(r.data?.allowed === true); })
      .catch(() => { if (!cancelled) setAllowed(false); });
    return () => { cancelled = true; };
  }, []);

  // Load data once allowed
  useEffect(() => {
    if (allowed !== true) return;
    let cancelled = false;
    setLoading(true);
    getManufacturerPurchases(3)
      .then((r) => { if (!cancelled) { setData(r.data); setLoading(false); } })
      .catch((e) => { if (!cancelled) { setError(e?.response?.data?.error || e.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [allowed]);

  const handleSort = useCallback((key) => {
    setSortBy((prev) => {
      if (prev === key) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      // Sensible default direction per column
      setSortDir(key === 'canonical' ? 'asc' : 'desc');
      return key;
    });
  }, []);

  const toggleRow = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const filteredSorted = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    let rows = data.manufacturers;
    if (q) {
      rows = rows.filter((m) =>
        m.canonical.toLowerCase().includes(q) ||
        (m.variants || []).some((v) => v.toLowerCase().includes(q))
      );
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      // Per-FY columns
      if (sortBy.startsWith('fy:')) {
        const fy = sortBy.slice(3);
        const aV = (metric === 'received' ? a.received_by_fy : a.ordered_by_fy)[fy] || 0;
        const bV = (metric === 'received' ? b.received_by_fy : b.ordered_by_fy)[fy] || 0;
        return (aV - bV) * dir;
      }
      const aV = a[sortBy];
      const bV = b[sortBy];
      if (typeof aV === 'string') return aV.localeCompare(bV) * dir;
      return ((aV || 0) - (bV || 0)) * dir;
    });
  }, [data, search, sortBy, sortDir, metric]);

  if (allowed === null) {
    return (
      <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <CircularProgress size={20} /><Typography>Checking access…</Typography>
      </Box>
    );
  }
  if (allowed === false) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Manufacturer Purchases is restricted to executive leadership.</Alert>
      </Box>
    );
  }

  const fiscal_years = data?.fiscal_years || [];
  const totalRecv = filteredSorted.reduce((s, m) => s + (m.received_total || 0), 0);
  const totalOrd  = filteredSorted.reduce((s, m) => s + (m.ordered_total || 0), 0);
  const totalWip  = filteredSorted.reduce((s, m) => s + (m.wip_forecast || 0), 0);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 300, color: '#0D2C54', letterSpacing: '-0.5px' }}>
            Manufacturer Purchases
          </Typography>
          <Typography variant="body2" sx={{ color: '#8898A4', mt: 0.5 }}>
            {data?.meta?.manufacturer_count ?? 0} manufacturers · last 3 fiscal years (Oct 1 – Sep 30) · current = {data?.meta?.current_fy}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            label="Search"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Lutron, Sonos, Samsung…"
            sx={{ width: 220 }}
          />
          <ToggleButtonGroup
            value={metric}
            exclusive
            size="small"
            onChange={(_, v) => v && setMetric(v)}
          >
            <ToggleButton value="received">Received</ToggleButton>
            <ToggleButton value="ordered">Ordered</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {/* Summary band */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Chip label={`Received ${data?.meta?.current_fy}: ${fmtMoney(filteredSorted.reduce((s, m) => s + (m.received_by_fy?.[data.meta.current_fy] || 0), 0))}`} sx={{ bgcolor: '#e8f5e9', color: '#1b5e20' }} />
        <Chip label={`3-Year Received: ${fmtMoney(totalRecv)}`} sx={{ bgcolor: '#e3f2fd', color: '#0d47a1' }} />
        <Chip label={`3-Year Ordered: ${fmtMoney(totalOrd)}`} sx={{ bgcolor: '#fff3e0', color: '#e65100' }} />
        <Chip
          label={`WIP Forecast: ${fmtMoney(totalWip)}`}
          icon={<TrendingUpIcon style={{ color: '#7b1fa2' }} />}
          sx={{ bgcolor: '#f3e5f5', color: '#4a148c' }}
        />
        {data?.meta?.wip_coverage_pct != null && (
          <Tooltip title={data.meta.wip_caveat || ''}>
            <Chip
              label={`Forecast Coverage: ${data.meta.wip_coverage_pct}%`}
              size="small"
              sx={{
                bgcolor: data.meta.wip_coverage_pct < 50 ? '#ffebee' : (data.meta.wip_coverage_pct < 80 ? '#fff3e0' : '#e8f5e9'),
                color:   data.meta.wip_coverage_pct < 50 ? '#c62828' : (data.meta.wip_coverage_pct < 80 ? '#e65100' : '#1b5e20'),
              }}
            />
          </Tooltip>
        )}
      </Box>
      {data?.meta?.wip_coverage_pct != null && data.meta.wip_coverage_pct < 80 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>WIP Forecast is incomplete.</strong> Only {data.meta.wip_coverage_pct}% of
          quoted units in active WIP could be priced via Sage cost lookup.
          D-Tools and Sage use different identifiers for many vendors (notably Crestron),
          so the actual WIP value is significantly higher than the figure shown.
          To make this accurate, ACS would need to store D-Tools proposal pricing locally
          — a policy decision currently disabled.
        </Alert>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4 }}>
          <CircularProgress size={20} /><Typography>Loading purchase data…</Typography>
        </Box>
      )}

      {!loading && data && (
        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: '#f5f5f5' } }}>
                  <TableCell sx={{ width: 32 }} />
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'canonical'}
                      direction={sortBy === 'canonical' ? sortDir : 'asc'}
                      onClick={() => handleSort('canonical')}
                    >
                      Manufacturer
                    </TableSortLabel>
                  </TableCell>
                  {fiscal_years.map((fy) => (
                    <TableCell key={fy} align="right">
                      <TableSortLabel
                        active={sortBy === `fy:${fy}`}
                        direction={sortBy === `fy:${fy}` ? sortDir : 'desc'}
                        onClick={() => handleSort(`fy:${fy}`)}
                      >
                        {fy}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                  <TableCell align="right">
                    <TableSortLabel
                      active={sortBy === (metric === 'received' ? 'received_total' : 'ordered_total')}
                      direction={sortDir}
                      onClick={() => handleSort(metric === 'received' ? 'received_total' : 'ordered_total')}
                    >
                      3-Year Total
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={sortBy === 'wip_forecast'}
                      direction={sortBy === 'wip_forecast' ? sortDir : 'desc'}
                      onClick={() => handleSort('wip_forecast')}
                    >
                      WIP Forecast
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredSorted.map((m) => {
                  const id = m.canonical;
                  const isOpen = expanded.has(id);
                  const byFy = metric === 'received' ? m.received_by_fy : m.ordered_by_fy;
                  const total = metric === 'received' ? m.received_total : m.ordered_total;
                  return (
                    <Fragment key={id}>
                      <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => toggleRow(id)}>
                        <TableCell sx={{ p: 0.5 }}>
                          <IconButton size="small">
                            {isOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                          </IconButton>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {m.canonical}
                          {m.variants_count > 1 && (
                            <Tooltip title={`Variants: ${m.variants.join(', ')}`}>
                              <Chip size="small" label={`${m.variants_count}`} sx={{ ml: 1, height: 18, fontSize: '0.65rem', bgcolor: '#e3f2fd', color: '#1565c0' }} />
                            </Tooltip>
                          )}
                        </TableCell>
                        {fiscal_years.map((fy) => {
                          const v = byFy[fy] || 0;
                          return (
                            <TableCell key={fy} align="right" sx={{ fontFamily: 'monospace', color: v === 0 ? 'text.disabled' : 'text.primary' }}>
                              <Tooltip title={fmtMoneyExact(v)} placement="top">
                                <span>{fmtMoney(v)}</span>
                              </Tooltip>
                            </TableCell>
                          );
                        })}
                        <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
                          <Tooltip title={fmtMoneyExact(total)} placement="top">
                            <span>{fmtMoney(total)}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace', color: m.wip_forecast > 0 ? '#7b1fa2' : 'text.disabled' }}>
                          <Tooltip
                            title={
                              m.wip_coverage_pct == null
                                ? 'No WIP items for this manufacturer'
                                : `${fmtMoneyExact(m.wip_forecast)} — priced ${m.wip_priced_qty} of ${m.wip_priced_qty + m.wip_unpriced_qty} units (${m.wip_coverage_pct}% coverage). Unpriced units couldn't be matched to a Sage cost.`
                            }
                            placement="top"
                          >
                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end' }}>
                              <span>{fmtMoney(m.wip_forecast)}</span>
                              {m.wip_coverage_pct != null && m.wip_coverage_pct < 80 && (
                                <Chip
                                  size="small"
                                  label={`${m.wip_coverage_pct}%`}
                                  sx={{
                                    height: 16,
                                    fontSize: '0.6rem',
                                    bgcolor: m.wip_coverage_pct < 30 ? '#ffebee' : '#fff3e0',
                                    color:   m.wip_coverage_pct < 30 ? '#c62828' : '#e65100',
                                  }}
                                />
                              )}
                            </Box>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                      {isOpen && (
                        <TableRow>
                          <TableCell colSpan={3 + fiscal_years.length + 1} sx={{ p: 0, borderBottom: '2px solid #e0e0e0' }}>
                            <QuarterDetail row={m} fiscal_years={fiscal_years} metric={metric} />
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
                {filteredSorted.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={3 + fiscal_years.length + 1} align="center" sx={{ py: 4, color: 'text.disabled' }}>
                      No manufacturers match your search.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {data && (
        <Typography variant="caption" sx={{ display: 'block', mt: 2, color: 'text.disabled' }}>
          Source: Sage 300 PO header/detail (received vs revised-ordered values), joined to itmmastr manufacturer.
          WIP Forecast = open D-Tools proposal items × Sage most-recent-cost.
          Names with multiple variants merged (Crestron / Crestron Electronics → one row); click the variant chip to inspect.
        </Typography>
      )}
    </Box>
  );
}
